import { motion } from 'framer-motion'
import './FloatingActionButton.css'

/**
 * FloatingActionButton - A circular button with glow effects and satisfying animations
 * 
 * @param {string} icon - Emoji or icon to display
 * @param {string} label - Accessible label for screen readers
 * @param {function} onClick - Click handler
 * @param {string} variant - 'primary' (cyan) or 'secondary' (amber)
 * @param {string} size - 'sm', 'md', or 'lg'
 * @param {string} position - 'bottom-right', 'bottom-left', 'top-right', 'top-left', or 'relative'
 * @param {boolean} disabled - Whether the button is disabled
 * @param {string} className - Additional CSS classes
 */
export function FloatingActionButton({
  icon = '+',
  label = 'Action button',
  onClick,
  variant = 'primary',
  size = 'md',
  position = 'relative',
  disabled = false,
  className = '',
  ...props
}) {
  const buttonVariants = {
    idle: {
      scale: 1,
      rotate: 0,
    },
    hover: {
      scale: 1.1,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 10,
      },
    },
    tap: {
      scale: 0.95,
      rotate: [0, -5, 5, -5, 0],
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 10,
        rotate: {
          duration: 0.3,
          ease: 'easeInOut',
        },
      },
    },
  }

  const glowVariants = {
    idle: {
      opacity: 0.5,
      scale: 1,
    },
    hover: {
      opacity: 1,
      scale: 1.2,
      transition: {
        duration: 0.2,
      },
    },
    tap: {
      opacity: 0.8,
      scale: 1.1,
    },
  }

  return (
    <motion.button
      className={`fab fab--${variant} fab--${size} fab--${position} ${className}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      variants={buttonVariants}
      initial="idle"
      whileHover="hover"
      whileTap="tap"
      {...props}
    >
      {/* Glow layer */}
      <motion.span 
        className="fab__glow"
        variants={glowVariants}
      />
      
      {/* Button content */}
      <span className="fab__icon">{icon}</span>
    </motion.button>
  )
}

export default FloatingActionButton

